import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { AppShell } from "@/components/AppShell";
import { CardArt } from "@/components/OrderCard";
import { getDemoOrder, displayId } from "@/lib/marketplace";
import { Price } from "@/lib/currency";
import { ArrowLeft, ArrowRight, CheckCircle2, Plus, Sparkles } from "lucide-react";

export const Route = createFileRoute("/demo/$id")({
  loader: ({ params }) => {
    const order = getDemoOrder(params.id);
    if (!order) throw notFound();
    return { order };
  },
  notFoundComponent: () => (
    <AppShell><div className="panel p-10 text-center">Demo order not found.</div></AppShell>
  ),
  component: DemoWalkthrough,
});

const STEPS = [
  {
    title: "Buyer posts a buy order",
    body: "A buyer sets the card, set, condition, and a public target price. They can also set a hidden auto-accept and max ceiling — sellers never see these, but offers compete against them in the background.",
  },
  {
    title: "Sellers compete with offers",
    body: "Sellers submit fulfilment offers including price, shipping method, and (for graded cards) their cert number plus a direct link to the grading company's lookup page. The buyer sees offers ranked side-by-side with seller ratings and verifies each cert link independently before accepting.",
  },
  {
    title: "Buyer approves and pays the seller directly",
    body: "Once the buyer accepts, they check out via PayPal Goods & Services and pay the seller directly. Want To Buy does not hold, control, or release funds — buyer protection comes from PayPal.",
  },
  {
    title: "Seller verifies and ships",
    body: "Sellers upload a minimum of 4 timestamped photos of the card before the ship button unlocks. For orders over $100, the seller also uploads front + back photos of the sealed package. Tracking is monitored — no scans within 5 business days flags the order for review.",
  },
  {
    title: "Delivery confirmed",
    body: "When tracking confirms delivery, both sides rate each other and the order is permanently marked Sold. If anything's wrong with the item, the buyer opens a dispute through PayPal — Want To Buy supports the case with platform evidence (timestamped photos, message thread).",
  },
];

function DemoWalkthrough() {
  const { order } = Route.useLoaderData();
  const [step, setStep] = useState(0);
  const done = step >= STEPS.length;
  const current = STEPS[Math.min(step, STEPS.length - 1)];
  const progress = done ? 100 : ((step) / STEPS.length) * 100;

  return (
    <AppShell>
      <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">Browse</Link>
        <span>/</span>
        <span className="text-foreground">Demo · {displayId(order)}</span>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
        <div className="panel overflow-hidden">
          <div className="relative p-4">
            <span className="absolute left-6 top-6 z-10 inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow">
              <Sparkles className="h-3 w-3" /> Demo
            </span>
            <CardArt seed={order.imageSeed} imageUrl={order.imageUrl} isDemo />
          </div>
          <div className="border-t border-border p-4">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{order.tcg} · #{order.shortId}</span>
            <h1 className="mt-1 font-display text-xl font-bold leading-tight">{order.cardName}</h1>
            <p className="text-xs text-muted-foreground">{order.set} · #{order.cardNumber}</p>
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Target</div>
              <Price usd={order.targetPrice} size="lg" />
            </div>
          </div>
        </div>

        <div className="panel p-6">
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{done ? "Walkthrough complete" : `Step ${step + 1} of ${STEPS.length}`}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {!done ? (
            <>
              <h2 className="font-display text-2xl font-bold">{current.title}</h2>
              <p className="mt-3 text-sm text-muted-foreground sm:text-base">{current.body}</p>
              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  className="btn-ghost inline-flex items-center gap-1.5 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button
                  onClick={() => setStep((s) => s + 1)}
                  className="btn-primary inline-flex items-center gap-1.5 px-5 py-2 text-sm"
                >
                  {step === STEPS.length - 1 ? "Finish" : "Next"} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success/15 text-success">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h2 className="mt-4 font-display text-2xl font-bold">Ready to post your first real order?</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                That's the whole flow — no real payment required for the demo.
              </p>
              <Link
                to="/create-order"
                className="btn-primary mt-6 inline-flex items-center gap-2 px-6 py-3 text-sm"
              >
                <Plus className="h-4 w-4" /> Post a buy order
              </Link>
              <div className="mt-4">
                <button onClick={() => setStep(0)} className="text-xs text-muted-foreground hover:text-foreground">
                  Replay walkthrough
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
